import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs, TabList, TabPanels, TabPanel, Tab,
  Box, Flex, Heading, Text, Button, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, FormControl, FormLabel, FormErrorMessage, useDisclosure,
} from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { storiesApi } from '../api/client';
import { fetchWrapper } from '../helpers';
import { useTheme } from '../context/ThemeContext';
import BoxWrapper from '../components/BoxWrapper/BoxWrapper';
import Header from '../components/Header';
import StoriesTab from '../components/story-dashboard/StoriesTab';
import TemplatesTab from '../components/story-dashboard/TemplatesTab';
import MediaTab from '../components/story-dashboard/MediaTab';
import CustomDomainsPanel from '../components/CustomDomainsPanel';
import SdkConfigTab from '../components/story-dashboard/SdkConfigTab';

const TAB_KEYS = ['stories', 'templates', 'media', 'domains', 'sdk', 'brand', 'analytics'];

export default function Dashboard() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeNav, setActiveNav] = useState('stories');
  const [creating, setCreating] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [aiAllowed, setAiAllowed] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inputRef = useRef(null);

  useEffect(() => {
    const customer = JSON.parse(localStorage.getItem('customer') || '{}');
    const customerId = customer?.customer_id;
    if (!customerId) return;
    fetchWrapper.get(`${process.env.REACT_APP_API_URL}/customer/by-id?id=${customerId}`)
      .then(res => { setAiAllowed(res?.data?.ai_allowed !== false); })
      .catch(() => { setAiAllowed(true); });
  }, []);

  const tabIndex = TAB_KEYS.indexOf(activeNav);
  const handleTabChange = (index) => setActiveNav(TAB_KEYS[index]);

  function openCreateModal() {
    setStoryTitle('');
    setTitleError('');
    onOpen();
  }

  async function createStory() {
    const title = storyTitle.trim();
    if (!title) {
      setTitleError('Story name is required');
      return;
    }
    setTitleError('');
    onClose();
    setCreating(true);
    try {
      const story = await storiesApi.create({ title, publisherName: 'My Publisher' });
      toast.success('Story created!');
      navigate(`/marketing/story-builder/${story.id}/edit`);
    } catch {
      toast.error('Failed to create story');
      setCreating(false);
    }
  }

  return (
    <Box>
      <Header />
      <BoxWrapper>
        <Box>
          <Flex align="center" justify="space-between" py={5} borderBottom="1px solid" borderColor="gray.200">
            <Box>
              <Heading size="md" color="gray.800" lineHeight="1.3">Web Stories</Heading>
              <Text fontSize="13px" color="gray.500" mt="2px">Create, manage, and track AMP Web Stories</Text>
            </Box>
            <HStack spacing={3}>
              <Button
                onClick={openCreateModal}
                isLoading={creating}
                loadingText="Creating…"
                leftIcon={<Plus size={15} />}
                bg="var(--brand-primary)"
                color="#fff"
                size="sm"
                fontWeight={600}
                _hover={{ bg: '#3a56d4' }}
              >
                New Story
              </Button>
            </HStack>
          </Flex>

          <Tabs index={tabIndex} onChange={handleTabChange} variant="line" colorScheme="blue" isLazy>
            <TabList borderBottom="1px solid" borderColor="gray.200" >
              {['Stories', 'Templates', 'Media Library', 'Custom Domain', 'SDK Configuration'].map(label => (
                // 'Brand Guide', 'Analytics'
                <Tab key={label} fontSize="sm" fontWeight={500}
                  _selected={{ color: '#1e293b', fontWeight: 600, borderColor: 'var(--brand-primary)' }}>
                  {label}
                </Tab>
              ))}
            </TabList>

            <TabPanels px={0}>
              <TabPanel py={5} px={0}>
                <StoriesTab createStory={openCreateModal} creating={creating} aiAllowed={aiAllowed} />
              </TabPanel>
              <TabPanel py={5} px={0}>
                <TemplatesTab />
              </TabPanel>
              <TabPanel py={5} px={0}>
                <MediaTab />
              </TabPanel>
              <TabPanel py={5} px={0}>
                <CustomDomainsPanel tenantId={localStorage.getItem('api-key') || 'my-tenant'} />
              </TabPanel>
              <TabPanel py={5} px={0}>
                <SdkConfigTab />
              </TabPanel>
              {/* <TabPanel py={5}>
                <BrandTab />
              </TabPanel>
              <TabPanel py={5}>
                <AnalyticsTab />
              </TabPanel> */}
            </TabPanels>
          </Tabs>
        </Box>
      </BoxWrapper>

      <Modal isOpen={isOpen} onClose={onClose} initialFocusRef={inputRef} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="16px" boxShadow="0 20px 60px rgba(0,0,0,0.15)">
          <ModalHeader fontSize="16px" fontWeight={700} color="gray.800" pb={1}>
            New Story
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl isInvalid={!!titleError}>
              <FormLabel fontSize="13px" fontWeight={600} color="gray.600" mb={1}>
                Story name
              </FormLabel>
              <Input
                ref={inputRef}
                value={storyTitle}
                onChange={e => { setStoryTitle(e.target.value); if (titleError) setTitleError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') createStory(); }}
                placeholder="e.g. Summer Fashion Trends 2026"
                size="md"
                borderRadius="10px"
                borderColor={titleError ? 'red.400' : 'gray.200'}
                _focus={{ borderColor: titleError ? 'red.400' : 'var(--brand-primary)', boxShadow: titleError ? '0 0 0 3px rgba(245,101,101,0.12)' : '0 0 0 3px rgba(67,97,238,0.12)' }}
                fontSize="14px"
              />
              <FormErrorMessage fontSize="12px">{titleError}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter pt={0} gap={2}>
            <Button variant="ghost" size="sm" onClick={onClose} color="gray.500">
              Cancel
            </Button>
            <Button
              onClick={createStory}
              isLoading={creating}
              loadingText="Creating…"
              leftIcon={<Plus size={14} />}
              bg="var(--brand-primary)"
              color="#fff"
              size="sm"
              fontWeight={600}
              borderRadius="8px"
              _hover={{ bg: '#3a56d4' }}
            >
              Create Story
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
